/**
 * <EnumSelect> — bilingual dropdown for backend-CHECK-constrained
 * enum fields (M3X.UI-HYGIENE-PASS-2 PR 1).
 *
 * The single canonical control for any field whose backend has a
 * fixed value set. Replaces plain `<select>` with hardcoded options +
 * raw `<input>` for enum fields.
 *
 *   <EnumSelect
 *     kind="contract_period"
 *     value={contract.contract_period}
 *     onChange={(v) => setContractPeriod(v)}
 *     required
 *     showDescriptions
 *   />
 *
 * The option set comes from `lib/enums/catalog.ts`. Backend Pydantic
 * enums are the source of truth — `constitution/backend/
 * test_enum_catalog_sync.py` catches drift.
 */

import { ChevronDown } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { getEnumDefinition } from "../../lib/enums";
import type { EnumKind, EnumOption } from "../../lib/enums";

export interface EnumSelectProps {
  kind: EnumKind;
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  required?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  /** When true, render the selected option's description below the control. */
  showDescriptions?: boolean;
  /** Optional per-tenant filtering (e.g., hide CASH for tenants that ban it). */
  filterOptions?: (option: EnumOption) => boolean;
  className?: string;
}

export function EnumSelect({
  kind,
  value,
  onChange,
  required = false,
  disabled = false,
  ariaLabel,
  showDescriptions = false,
  filterOptions,
  className,
}: EnumSelectProps) {
  const { i18n } = useTranslation();
  const def = getEnumDefinition(kind);
  const isAr = i18n.language === "ar";

  const options = useMemo(
    () =>
      filterOptions ? def.options.filter(filterOptions) : def.options,
    [def.options, filterOptions],
  );

  const labelFor = (opt: EnumOption) => (isAr ? opt.labelAr : opt.labelEn);
  const descFor = (opt: EnumOption) =>
    isAr ? opt.descriptionAr : opt.description;

  const selected = value ? options.find((o) => o.value === value) ?? null : null;

  return (
    <div className={`relative ${className ?? ""}`}>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
        required={required}
        aria-label={ariaLabel}
        aria-required={required}
        className="block w-full appearance-none rounded-md border border-border-default bg-surface-default ps-3 pe-9 py-2 text-sm shadow-sm focus:border-brand focus:ring-1 focus:ring-brand disabled:bg-surface-sunken disabled:text-text-tertiary"
      >
        {def.allowEmpty && (
          <option value="">
            {isAr ? def.emptyLabelAr ?? "—" : def.emptyLabelEn ?? "—"}
          </option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {labelFor(opt)}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
      {showDescriptions && selected && descFor(selected) && (
        <p className="mt-1 text-xs text-text-tertiary">{descFor(selected)}</p>
      )}
    </div>
  );
}
